import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';
import { apiService } from '@/utils/api';
import { getApiErrorMessage } from '@/utils/apiError';
import { fetchPickerPages } from '@/components/modals/fetchPickerPages';
import type {
  ActiveFilter,
  AppearanceBase,
  AppearanceCreateInput,
  AppearanceDetail,
  AppearancePatchInput,
  ColorDropdownItem,
  ColorProperty,
  ModelColor,
  ModelColorCreateInput,
  ModelColorDetail,
  ModelColorPatchInput,
  PaletteDropdownItem,
  PaletteItem,
  PaletteKind,
} from './types';

export const APPEARANCES_KEY = 'appearances';
export const PALETTE_KEY = 'appearance-palette';

const PAGE_SIZE = 30;
const paletteUrl = (kind: PaletteKind) => `/api/v1/model-${kind}/`;
const COLOR_URL = '/api/v1/model-color/';

interface Page<T> {
  count: number;
  next: number | null;
  previous: number | null;
  results: T[];
}

export interface AppearanceListFilters {
  search?: string;
  isActive?: ActiveFilter;
  modelPattern?: string;
  modelFinish?: string;
}

export interface ConflictExtra {
  appearances?: { id: string; title: string }[];
  inactive?: ('model_pattern' | 'model_finish')[];
}

export const isConflict = (error: unknown) => (error as AxiosError)?.response?.status === 409;

export const conflictExtra = (error: unknown): ConflictExtra =>
  ((error as AxiosError<{ extraData?: ConflictExtra }>)?.response?.data?.extraData ??
    {}) as ConflictExtra;

/** Toasts errors of calls sent with `silentError` (used where a 409 has its own UI). */
export const toastUnlessConflict = (error: unknown) => {
  if (!isConflict(error)) toast.error(getApiErrorMessage(error));
};

export function useAppearanceList(modelId: string | null, filters: AppearanceListFilters = {}) {
  const { search, isActive = 'true', modelPattern, modelFinish } = filters;
  const query = useInfiniteQuery({
    queryKey: [APPEARANCES_KEY, 'list', modelId, { search, isActive, modelPattern, modelFinish }],
    enabled: !!modelId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam = 1 }) =>
      (
        await apiService.get<Page<AppearanceBase>>('/api/v1/appearance/left-panel/', {
          params: {
            model: modelId,
            page: pageParam,
            page_size: PAGE_SIZE,
            search: search || undefined,
            is_active: isActive,
            model_pattern: modelPattern,
            model_finish: modelFinish,
          },
        })
      ).data,
    getNextPageParam: (lastPage) => lastPage?.next ?? undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.results) ?? [];
  const count = query.data?.pages[0]?.count ?? 0;
  return { ...query, items, count };
}

export function useAppearanceDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: [APPEARANCES_KEY, 'detail', id],
    enabled: !!id,
    refetchOnWindowFocus: false,
    queryFn: async () => (await apiService.get<AppearanceDetail>(`/api/v1/appearance/${id}/`)).data,
  });
}

export function useAppearanceDropdown(modelId: string | null, enabled = true) {
  return useQuery({
    queryKey: [APPEARANCES_KEY, 'dropdown', modelId],
    enabled: !!modelId && enabled,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      (
        await apiService.get<AppearanceBase[]>('/api/v1/appearance/dropdown/', {
          params: { model: modelId, pagination: false },
        })
      ).data,
  });
}

export function useColorProperties() {
  return useQuery({
    queryKey: ['color-properties'],
    staleTime: Infinity,
    queryFn: async () =>
      (await apiService.get<ColorProperty[]>('/api/v1/appearance/color-properties/')).data,
  });
}

export function usePalette(
  kind: PaletteKind,
  modelId: string | null,
  { search, isActive = 'true' }: { search?: string; isActive?: ActiveFilter } = {}
) {
  return useQuery({
    queryKey: [PALETTE_KEY, kind, 'list', modelId, { search, isActive }],
    enabled: !!modelId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    queryFn: ({ signal }) =>
      fetchPickerPages<PaletteItem>(
        paletteUrl(kind),
        { model: modelId, page_size: 100, search: search || undefined, is_active: isActive },
        signal ?? new AbortController().signal
      ),
  });
}

export function usePaletteItem(kind: PaletteKind, id: string | null | undefined) {
  return useQuery({
    queryKey: [PALETTE_KEY, kind, 'detail', id],
    enabled: !!id,
    refetchOnWindowFocus: false,
    queryFn: async () => (await apiService.get<PaletteItem>(`${paletteUrl(kind)}${id}/`)).data,
  });
}

export function usePaletteDropdown(kind: PaletteKind, modelId: string | null) {
  return useQuery({
    queryKey: [PALETTE_KEY, kind, 'dropdown', modelId],
    enabled: !!modelId,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      (
        await apiService.get<PaletteDropdownItem[]>(`${paletteUrl(kind)}dropdown/`, {
          params: { model: modelId, pagination: false },
        })
      ).data,
  });
}

export function useColors(
  modelId: string | null,
  { search, isActive = 'true' }: { search?: string; isActive?: ActiveFilter } = {}
) {
  return useQuery({
    queryKey: [PALETTE_KEY, 'color', 'list', modelId, { search, isActive }],
    enabled: !!modelId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    queryFn: ({ signal }) =>
      fetchPickerPages<ModelColor>(
        COLOR_URL,
        { model: modelId, page_size: 100, search: search || undefined, is_active: isActive },
        signal ?? new AbortController().signal
      ),
  });
}

export function useColor(id: string | null | undefined) {
  return useQuery({
    queryKey: [PALETTE_KEY, 'color', 'detail', id],
    enabled: !!id,
    refetchOnWindowFocus: false,
    queryFn: async () => (await apiService.get<ModelColorDetail>(`${COLOR_URL}${id}/`)).data,
  });
}

export function useColorDropdown(modelId: string | null) {
  return useQuery({
    queryKey: [PALETTE_KEY, 'color', 'dropdown', modelId],
    enabled: !!modelId,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      (
        await apiService.get<ColorDropdownItem[]>(`${COLOR_URL}dropdown/`, {
          params: { model: modelId, pagination: false },
        })
      ).data,
  });
}

/** Appearances and palettes reference each other (codes, titles, `appearance_count`). */
function useInvalidateAppearanceData() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries(APPEARANCES_KEY);
    queryClient.invalidateQueries(PALETTE_KEY);
  };
}

export function useCreateAppearance() {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: async (input: AppearanceCreateInput) =>
      (await apiService.post<AppearanceBase>('/api/v1/appearance/', input, { silentError: true }))
        .data,
    onSuccess: invalidate,
  });
}

export function usePatchAppearance() {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: AppearancePatchInput }) =>
      (
        await apiService.patch<AppearanceDetail>(`/api/v1/appearance/${id}/`, input, {
          silentError: true,
        })
      ).data,
    onSuccess: invalidate,
  });
}

export function useToggleAppearance() {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiService.post(
        `/api/v1/appearance/${id}/${active ? 'activate' : 'deactivate'}/`,
        {},
        { silentError: true }
      ),
    onSuccess: invalidate,
  });
}

export function useAddToPalette(kind: PaletteKind) {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: async ({
      model,
      classifiedSolution,
    }: {
      model: string;
      classifiedSolution: string;
    }) => {
      const response = await apiService.post<PaletteItem>(paletteUrl(kind), {
        model,
        classified_solution: classifiedSolution,
      });
      return { item: response.data, created: response.status === 201 };
    },
    onSuccess: invalidate,
  });
}

export function useCreateColor() {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: async (input: ModelColorCreateInput) =>
      (await apiService.post<ModelColor>(COLOR_URL, input, { silentError: true })).data,
    onSuccess: invalidate,
  });
}

export function usePatchColor() {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ModelColorPatchInput }) =>
      (await apiService.patch<ModelColor>(`${COLOR_URL}${id}/`, input, { silentError: true })).data,
    onSuccess: invalidate,
  });
}

export function useToggleColor() {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiService.post(
        `${COLOR_URL}${id}/${active ? 'activate' : 'deactivate'}/`,
        {},
        { silentError: true }
      ),
    onSuccess: invalidate,
  });
}

export function usePatchPaletteItem(kind: PaletteKind) {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: async ({ id, customTitle }: { id: string; customTitle: string | null }) =>
      (
        await apiService.patch<PaletteItem>(`${paletteUrl(kind)}${id}/`, {
          custom_title: customTitle,
        })
      ).data,
    onSuccess: invalidate,
  });
}

export function useTogglePaletteItem(kind: PaletteKind) {
  const invalidate = useInvalidateAppearanceData();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiService.post(
        `${paletteUrl(kind)}${id}/${active ? 'activate' : 'deactivate'}/`,
        {},
        { silentError: true }
      ),
    onSuccess: invalidate,
  });
}
